/*
 * EGR — core library
 * Formatting, catalog lookups, pricing, the bag, and rendered artwork
 * (vials, residue maps, chromatograms, product cards).
 */
(function () {
  "use strict";

  const D = window.EGR_DATA;
  const P = D.promo;
  const EGR = (window.EGR = window.EGR || {});
  EGR.data = D;

  /* ---------------------------------------------------------------- utils */
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: (window.EGR_SHOP && window.EGR_SHOP.currency) || "USD" });
  const money = (n) => usd.format(n);
  const moneyShort = (n) => usd.format(n).replace(/\.00$/, "");
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  function fmtDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    return `${d} ${MONTHS[m - 1]} ${y}`;
  }
  function decimals(n) { const s = String(n); const i = s.indexOf("."); return i < 0 ? 0 : s.length - i - 1; }
  function fmtNum(n, d) { return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }); }
  function fmtMassPair(observed, theoretical) {
    const d = Math.max(decimals(observed), decimals(theoretical));
    return [fmtNum(observed, d), fmtNum(theoretical, d)];
  }
  const fmtPct = (n) => `${Number(n).toFixed(2)} %`;
  function hash(str) { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function rng(seed) { let a = seed >>> 0; return function () { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
  function qs(name) { return new URLSearchParams(location.search).get(name); }
  function storage(key, val) {
    try {
      if (val === undefined) { const raw = localStorage.getItem(key); return raw == null ? null : JSON.parse(raw); }
      if (val === null) localStorage.removeItem(key); else localStorage.setItem(key, JSON.stringify(val));
    } catch (e) { return null; }
  }
  function hexRgb(h) { h = h.replace("#", ""); if (h.length === 3) h = h.split("").map((c) => c + c).join(""); const n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
  function mix(a, b, t) { const x = hexRgb(a), y = hexRgb(b); return "#" + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, "0")).join(""); }
  function isDark(hex) { const [r, g, b] = hexRgb(hex); return (0.299 * r + 0.587 * g + 0.114 * b) < 140; }

  EGR.u = { $, $$, esc, round2, money, moneyShort, fmtDate, fmtNum, fmtMassPair, fmtPct, hash, rng, qs, storage, mix };

  /* ---------------------------------------------------------------- icons */
  const ICONS = {
    search: '<circle cx="11" cy="11" r="7"/><path d="m20.5 20.5-4.6-4.6"/>',
    bag: '<path d="M5.5 8.5h13l-.9 11.2a1.5 1.5 0 0 1-1.5 1.3H7.9a1.5 1.5 0 0 1-1.5-1.3L5.5 8.5Z"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5"/>',
    user: '<circle cx="12" cy="8.5" r="4"/><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>',
    menu: '<path d="M4 9h16M4 15h16"/>',
    close: '<path d="M6 6l12 12M18 6 6 18"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    minus: '<path d="M5 12h14"/>',
    check: '<path d="m5 12.5 4.2 4.2L19 7"/>',
    chevR: '<path d="m9 5 7 7-7 7"/>',
    chevL: '<path d="m15 5-7 7 7 7"/>',
    chevD: '<path d="m6 9 6 6 6-6"/>',
    arrow: '<path d="M4 12h15M13 6l6 6-6 6"/>',
    flask: '<path d="M9 3h6"/><path d="M10 3v6.2L4.6 18.4A1.8 1.8 0 0 0 6.2 21h11.6a1.8 1.8 0 0 0 1.6-2.6L14 9.2V3"/><path d="M7.2 15h9.6"/>',
    atom: '<circle cx="12" cy="12" r="1.2"/><ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(45 12 12)"/><ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(-45 12 12)"/>',
    fileCheck: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"/><path d="M14 3v5h5"/><path d="m9 14.5 2 2 4-4"/>',
    shield: '<path d="M12 3 5 6v5.5c0 4.3 2.9 8 7 9.5 4.1-1.5 7-5.2 7-9.5V6l-7-3Z"/><path d="m9 12 2 2 4-4"/>',
    snow: '<path d="M12 2.5v19M4 7l16 10M4 17 20 7"/><path d="m9.5 4 2.5 2 2.5-2M9.5 20l2.5-2 2.5 2M3.6 10.2l3 .3-1 2.9M20.4 13.8l-3-.3 1-2.9M5.6 16.8l1-2.9-3-.3M18.4 7.2l-1 2.9 3 .3"/>',
    truck: '<path d="M2.5 6.5h11v10h-11z"/><path d="M13.5 9.5h4l3.5 3.5v3.5h-7.5"/><circle cx="6.5" cy="17.5" r="1.9"/><circle cx="17" cy="17.5" r="1.9"/>',
    drop: '<path d="M12 3s6.5 6.6 6.5 11.3a6.5 6.5 0 0 1-13 0C5.5 9.6 12 3 12 3Z"/>',
    swap: '<path d="M4 8.5h14l-3.5-3.5M20 15.5H6l3.5 3.5"/>',
    zap: '<path d="M13 2.5 4.5 13.5H11l-1 8 8.5-11H12l1-8Z"/>',
    percent: '<path d="M19 5 5 19"/><circle cx="7" cy="7" r="2.4"/><circle cx="17" cy="17" r="2.4"/>',
    chat: '<path d="M20.5 12a8.5 8.5 0 0 1-12.3 7.6L3.5 20.5l1-4.4A8.5 8.5 0 1 1 20.5 12Z"/>',
    box: '<path d="M21 7.5 12 3 3 7.5v9L12 21l9-4.5v-9Z"/><path d="m3 7.5 9 4.5 9-4.5M12 12v9"/>',
    layers: '<path d="m12 3 9 4.8-9 4.8-9-4.8L12 3Z"/><path d="m3 12.2 9 4.8 9-4.8M3 16.6l9 4.8 9-4.8"/>',
    sparkle: '<path d="M12 3c.6 4.5 2.5 6.4 7 7-4.5.6-6.4 2.5-7 7-.6-4.5-2.5-6.4-7-7 4.5-.6 6.4-2.5 7-7Z"/><path d="M19 15.5c.2 1.6.9 2.3 2.5 2.5-1.6.2-2.3.9-2.5 2.5-.2-1.6-.9-2.3-2.5-2.5 1.6-.2 2.3-.9 2.5-2.5Z"/>',
    clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
    mail: '<rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="m3.5 7 8.5 6 8.5-6"/>',
    building: '<path d="M4 21V5a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 15 5v16M15 9.5h3.5A1.5 1.5 0 0 1 20 11v10M3 21h18"/><path d="M8 7.5h3M8 11h3M8 14.5h3"/>',
    print: '<path d="M7 9V3.5h10V9"/><rect x="3.5" y="9" width="17" height="8" rx="2"/><path d="M7 14h10v6.5H7z"/>',
    info: '<circle cx="12" cy="12" r="8.5"/><path d="M12 11v5.5M12 7.8v.1"/>',
    warn: '<path d="M12 3.5 2.5 20h19L12 3.5Z"/><path d="M12 10v4.5M12 17.4v.1"/>',
    star: '<path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5Z"/>',
    tag: '<path d="M3.5 12.6V4.5a1 1 0 0 1 1-1h8.1l8 8a1.5 1.5 0 0 1 0 2.1l-6 6a1.5 1.5 0 0 1-2.1 0l-9-9Z"/><circle cx="8" cy="8" r="1.4"/>',
    calendar: '<rect x="3.5" y="5" width="17" height="15.5" rx="2"/><path d="M3.5 10h17M8 3v4M16 3v4"/>',
    book: '<path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v15H5.5A1.5 1.5 0 0 0 4 19.5v-15Z"/><path d="M4 19.5A1.5 1.5 0 0 0 5.5 21H19v-3"/>',
    beaker: '<path d="M5 3h14M6.5 3v15.5A2.5 2.5 0 0 0 9 21h6a2.5 2.5 0 0 0 2.5-2.5V3"/><path d="M6.5 12h11"/>',
    handshake: '<path d="m11 17 2 2a1.4 1.4 0 0 0 2-2"/><path d="m14 14 2.5 2.5a1.4 1.4 0 0 0 2-2l-3.9-3.9a2.8 2.8 0 0 0-4 0l-.9.9a1.4 1.4 0 0 1-2-2l2.8-2.8a4.2 4.2 0 0 1 5.2-.6l.5.3a3 3 0 0 0 1.6.4H21"/><path d="m21 15-1 1M3 4h8M3 4v9l6.5 6.5a1.4 1.4 0 0 0 2-2"/>',
    eye: '<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"/><circle cx="12" cy="12" r="3"/>',
    arrowUR: '<path d="M7 17 17 7M8.5 7H17v8.5"/>',
    pause: '<path d="M9 5.5v13M15 5.5v13"/>',
    play: '<path d="M8 5.5v13l10.5-6.5L8 5.5Z"/>',
    send: '<path d="M4 12 20 4l-5.5 16-3-6.5L4 12Z"/><path d="m11.5 13.5 3.8-3.8"/>',
    lock: '<rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/>',
    hex: '<path d="m12 3 7.8 4.5v9L12 21l-7.8-4.5v-9L12 3Z"/><circle cx="12" cy="12" r="2.2"/>',
    pin: '<path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z"/><circle cx="12" cy="10" r="2.4"/>',
    pkg: '<path d="M21 7.5 12 3 3 7.5v9L12 21l9-4.5v-9Z"/><path d="m3 7.5 9 4.5 9-4.5M12 12v9M7.5 5.2l9 4.6"/>',
    clipboard: '<rect x="5" y="4.5" width="14" height="16.5" rx="2"/><path d="M9 4.5V3h6v1.5"/><path d="m9 13 2 2 4-4"/>',
    globe: '<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.4 2.4 3.5 5.2 3.5 8.5s-1.1 6.1-3.5 8.5c-2.4-2.4-3.5-5.2-3.5-8.5S9.6 5.9 12 3.5Z"/>'
  };
  function icon(name, cls) {
    return `<svg class="icon-stroke ${cls || ""}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[name] || ""}</svg>`;
  }
  EGR.icon = icon;
  /* EGR wordmark: wide "EGR" letters with a chrome fill, a sheen that sweeps
     on hover, and the hexagon molecule whose nodes spring open on hover
     (see .egr-logo in egr.css). opts.tone: "ink" (default) or "light". */
  let logoId = 0;
  EGR.logo = function (cls, opts) {
    opts = opts || {};
    const id = "egl" + (++logoId);
    const light = opts.tone === "light";
    const tag = opts.tagline === false ? "" : `<text class="egr-logo-tag" x="1" y="57" textLength="196" lengthAdjust="spacingAndGlyphs">ENHANCED GENETIC RESEARCH</text>`;
    const nodes = [[166, 10], [180, 18], [180, 34], [166, 42], [152, 34], [152, 18]];
    const hex = nodes.map((n) => n.join(",")).join(" ");
    const dots = nodes.concat([[166, 1.5], [194, 10]]).map(([x, y], i) => `<circle class="n n${i}" cx="${x}" cy="${y}" r="${i > 5 ? 2.6 : 2.2}"/>`).join("");
    return `<svg class="egr-logo ${light ? "is-light" : ""} ${cls || ""}" viewBox="0 0 200 ${tag ? 60 : 46}" role="img" aria-label="EGR, Enhanced Genetic Research" focusable="false">
<defs>
<linearGradient id="${id}m" x1="0" y1="0" x2="0" y2="1">${light
      ? `<stop offset="0" stop-color="#ffffff"/><stop offset=".48" stop-color="#d9e1ea"/><stop offset=".52" stop-color="#aebbc9"/><stop offset="1" stop-color="#eef3f8"/>`
      : `<stop offset="0" stop-color="#3a4a5e"/><stop offset=".46" stop-color="#0f1a26"/><stop offset=".54" stop-color="#1d2b3c"/><stop offset="1" stop-color="#52657b"/>`}</linearGradient>
<linearGradient id="${id}s" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset=".5" stop-color="#fff" stop-opacity=".9"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>
<clipPath id="${id}c"><text x="0" y="40" class="egr-logo-word" textLength="142" lengthAdjust="spacingAndGlyphs">EGR</text></clipPath>
</defs>
<text x="0" y="40" class="egr-logo-word" textLength="142" lengthAdjust="spacingAndGlyphs" fill="url(#${id}m)" stroke="url(#${id}m)">EGR</text>
<g clip-path="url(#${id}c)" aria-hidden="true"><rect class="egr-logo-sheen" x="-60" y="0" width="46" height="46" fill="url(#${id}s)"/></g>
<g class="egr-logo-mol" aria-hidden="true">
<polygon class="h" points="${hex}"/>
<path class="b" d="M166 10V1.5M180 18l14-8"/>
${dots}
</g>
${tag}
</svg>`;
  };

  /* -------------------------------------------------------------- catalog */
  const products = D.products;
  const byHandle = {};
  const variantIndex = {};
  const lotIndex = {};
  products.forEach((p) => {
    byHandle[p.handle] = p;
    p.variants.forEach((v) => {
      variantIndex[v.id] = { product: p, variant: v };
      v.lots.forEach((l) => { lotIndex[l.number] = { lot: l, product: p, variant: v }; });
    });
  });
  const allLots = Object.values(lotIndex).sort((a, b) => (b.lot.testedAt.localeCompare(a.lot.testedAt)) || b.lot.number.localeCompare(a.lot.number));
  const activeLot = (v) => v.lots.find((l) => l.status === "active") || v.lots[0];
  const catLabel = (id) => (D.categories.find((c) => c.id === id) || {}).label || id;
  const isBlend = (p) => !!(p.components && p.components.length);

  function computeStats() {
    const released = allLots.filter((x) => x.lot.status === "active");
    const purities = released.map((x) => x.lot.purity).filter((n) => n != null).sort((a, b) => a - b);
    const mid = Math.floor(purities.length / 2);
    const median = purities.length % 2 ? purities[mid] : (purities[mid - 1] + purities[mid]) / 2;
    return {
      released: released.length,
      total: allLots.length,
      median: median,
      newest: released[0],
      updated: released[0] ? released[0].lot.testedAt : null,
      compounds: products.filter((p) => p.kind !== "water").length
    };
  }
  const stats = computeStats();

  const featured = products.filter((p) => p.featuredRank).sort((a, b) => a.featuredRank - b.featuredRank);

  EGR.catalog = {
    products, byHandle, variantIndex, lotIndex, allLots, activeLot, catLabel, isBlend, stats, featured,
    product: (h) => byHandle[h],
    variant: (id) => variantIndex[id],
    lot: (n) => lotIndex[n],
    daily: () => P.daily.handles.map((h) => byHandle[h]).filter(Boolean),
    appearance(p) {
      const f = (p.spec.form || "").toLowerCase();
      if (p.kind === "water") return "Clear, colourless solution";
      if (f.includes("(blue)")) return "Blue lyophilized powder";
      if (f.includes("lyophilized")) return "White lyophilized powder";
      return p.spec.form;
    },
    sizes: (p) => p.variants.map((v) => v.title).join(" · ")
  };

  /* -------------------------------------------------------------- pricing */
  const pricing = {
    sitewideActive(now) { return !!(P.sitewide.enabled && (now || Date.now()) < Date.parse(P.sitewide.endsAt)); },
    sitewideEnds() { return Date.parse(P.sitewide.endsAt); },
    isDaily(p) { return !!(P.daily.enabled && P.daily.handles.indexOf(p.handle) >= 0); },
    dailyPrice(v) { return round2(v.price * (1 - P.daily.percent / 100)); },
    perMg(v, price) {
      if (!v.mg) return v.volumeMl ? `${money((price || v.price) / v.volumeMl)} / mL` : "";
      return `${money((price || v.price) / v.mg)} / mg`;
    },
    bulkTier(qty) { let t = null; P.bulk.tiers.forEach((x) => { if (qty >= x.min) t = x; }); return t; },
    nextBulkTier(qty) { return P.bulk.tiers.find((x) => qty < x.min) || null; },
    unitAt(v, pct) { return round2(v.price * (1 - pct / 100)); },
    line(p, v, qty) {
      const base = v.price;
      const retail = round2(base * qty);
      let pct = 0, label = "", group = "";
      if (this.sitewideActive()) { pct = P.sitewide.percent; label = P.sitewide.name; group = `${P.sitewide.name} (−${pct}%)`; }
      const bt = this.bulkTier(qty);
      if (bt && bt.percent > pct) { pct = bt.percent; label = `Bulk ${bt.min}+ units`; group = `Bulk ${bt.min}+ units (−${bt.percent}%)`; }
      let total;
      if (this.isDaily(p) && P.daily.percent > pct) {
        const du = Math.min(qty, P.daily.maxUnitsPerLine);
        total = du * round2(base * (1 - P.daily.percent / 100)) + (qty - du) * round2(base * (1 - pct / 100));
        label = du < qty ? `Today's list (${du} units)` : "Today's list";
        group = `Today's list (−${P.daily.percent}%)`;
        pct = P.daily.percent;
      } else {
        total = qty * round2(base * (1 - pct / 100));
      }
      total = round2(total);
      const on = total < retail;
      return { retail, total, discount: round2(retail - total), label: on ? label : "", group: on ? group : "", pct: on ? pct : 0 };
    },
    box(variantIds) {
      const retail = round2(variantIds.reduce((s, id) => s + (variantIndex[id] ? variantIndex[id].variant.price : 0), 0));
      const total = round2(retail * (1 - P.box.percent / 100));
      return { retail, total, discount: round2(retail - total) };
    },
    totals(lines) {
      const out = { lines: [], retail: 0, total: 0, savings: [], count: 0 };
      const handles = new Set();
      lines.forEach((l) => { if (l.t === "item" && variantIndex[l.v]) handles.add(variantIndex[l.v].product.handle); });
      let multi = null;
      P.multi.tiers.forEach((t) => { if (handles.size >= t.min) multi = t; });
      const savings = {};
      const addSaving = (label, amt) => { if (amt > 0) savings[label] = round2((savings[label] || 0) + amt); };
      lines.forEach((l) => {
        if (l.t === "item") {
          const ref = variantIndex[l.v];
          if (!ref) return;
          const pr = this.line(ref.product, ref.variant, l.q);
          let total = pr.total;
          let multiAmt = 0;
          if (multi && pr.discount === 0) { multiAmt = round2(pr.retail * multi.percent / 100); total = round2(total - multiAmt); }
          out.retail += pr.retail; out.total += total; out.count += l.q;
          addSaving(pr.group, pr.discount);
          if (multiAmt) addSaving(`Multi-buy · ${handles.size} compounds (−${multi.percent}%)`, multiAmt);
          out.lines.push(Object.assign({}, l, { product: ref.product, variant: ref.variant, pricing: pr, multi: multiAmt, lineTotal: total }));
        } else if (l.t === "box") {
          const pr = this.box(l.items);
          out.retail += pr.retail; out.total += pr.total; out.count += 1;
          addSaving(`Monthly box (−${P.box.percent}%)`, pr.discount);
          out.lines.push(Object.assign({}, l, { pricing: pr, lineTotal: pr.total }));
        }
      });
      out.retail = round2(out.retail);
      out.total = round2(out.total);
      out.savings = Object.keys(savings).map((k) => ({ label: k, amount: savings[k] }));
      out.discount = round2(out.retail - out.total);
      out.freeShipping = out.total >= P.freeShippingOver;
      out.toFree = round2(Math.max(0, P.freeShippingOver - out.total));
      return out;
    }
  };
  EGR.pricing = pricing;

  /* ------------------------------------------------------------------ bag */
  const BAG_KEY = "egr_bag_v1";
  const listeners = [];
  let bag = (storage(BAG_KEY) || []).filter((l) => (l.t === "item" && variantIndex[l.v]) || (l.t === "box" && Array.isArray(l.items)));
  function save() { storage(BAG_KEY, bag); listeners.forEach((fn) => fn(bag)); }
  EGR.bag = {
    lines: () => bag.slice(),
    count: () => bag.reduce((s, l) => s + (l.t === "item" ? l.q : 1), 0),
    add(variantId, qty) {
      qty = Math.max(1, Math.min(999, parseInt(qty, 10) || 1));
      const ex = bag.find((l) => l.t === "item" && l.v === variantId);
      if (ex) ex.q = Math.min(999, ex.q + qty); else bag.push({ t: "item", v: variantId, q: qty, key: "i-" + variantId });
      save();
    },
    addBox(items) {
      bag.push({ t: "box", items: items.slice(), key: "b-" + Date.now().toString(36), q: 1 });
      save();
    },
    setQty(key, qty) {
      const l = bag.find((x) => x.key === key);
      if (!l) return;
      qty = parseInt(qty, 10);
      if (!qty || qty < 1) { bag = bag.filter((x) => x.key !== key); } else { l.q = Math.min(999, qty); }
      save();
    },
    remove(key) { bag = bag.filter((x) => x.key !== key); save(); },
    clear() { bag = []; save(); },
    totals: () => pricing.totals(bag),
    on(fn) { listeners.push(fn); }
  };
  window.addEventListener("storage", (e) => { if (e.key === BAG_KEY) { bag = storage(BAG_KEY) || []; listeners.forEach((fn) => fn(bag)); } });

  /* ---------------------------------------------------------------- vials */
  /* EGR label vial, drawn to match the brand photograph: clear glass, chrome
     flip cap, black label band with the silver EGR mark, the product name in
     white, a coloured accent bar and molecule, and the research-use lines. */
  let uid = 0;
  const ACCENTS = { sky: "#3d7bff", lilac: "#8b6cff", aqua: "#22c1dc", mint: "#2fcf8f", butter: "#f5b73b", blush: "#f2569b", peach: "#ff8b4d" };
  const accentOf = (p) => (p && (p.accent || ACCENTS[p.tone])) || ACCENTS.sky;
  EGR.accent = accentOf;
  const SANS = "'Manrope', 'Segoe UI', system-ui, sans-serif";
  const WIDE = "'Michroma', 'Manrope', system-ui, sans-serif";
  /* Product photos come from Shopify (the variant's image, else the product's).
     Shopify's image server resizes on the fly: small for cards, large for the
     product page. With no photo in Shopify, the vial is drawn. */
  // The Shopify photo (resized by Shopify's CDN), else the photo that ships with the theme.
  function photoFor(p, v, large) {
    const src = (v && v.image) || p.image;
    if (src) return src + (src.indexOf("?") >= 0 ? "&" : "?") + "width=" + (large ? 1200 : 640);
    const own = (v && v.photo) || (p.variants && p.variants[0] && p.variants[0].photo);
    return own ? own[large ? 1 : 0] : "";
  }
  EGR.photoFor = photoFor;
  function vial(p, opts) {
    opts = opts || {};
    const id = "v" + (++uid);
    const v = opts.variant || p.variants[0];
    const photo = photoFor(p, v, opts.large);
    if (photo) {
      const ph = opts.shadow ? 206 : 196;
      const label = opts.title ? `role="img" aria-label="${esc(opts.title)}"` : `aria-hidden="true"`;
      return `<svg class="vial vial-photo ${opts.cls || ""}" viewBox="0 0 120 ${ph}" ${label} xmlns="http://www.w3.org/2000/svg"><image href="${esc(photo)}" x="0" y="0" width="120" height="196" preserveAspectRatio="xMidYMax meet"/></svg>`;
    }
    const lt = activeLot(v);
    const water = p.kind === "water";
    const acc = accentOf(p);
    const name = String(p.short).toUpperCase();
    const n = name.length;
    const fs = n <= 5 ? 13 : n <= 7 ? 11.2 : n <= 9 ? 9.6 : n <= 11 ? 8.2 : 7.4;
    const fit = n * fs * 0.68 > 58 ? ` textLength="58" lengthAdjust="spacingAndGlyphs"` : "";
    const blue = /\(blue\)/.test(p.spec.form || "");
    const powderTop = blue ? "#7fc2ff" : "#ffffff";
    const powderBot = blue ? "#2a78d8" : "#dfe5ec";
    const title = opts.title ? `<title>${esc(opts.title)}</title>` : "";
    const aria = opts.title ? `role="img" aria-label="${esc(opts.title)}"` : `aria-hidden="true"`;
    const body = "M42 40h36v8c0 6 26 7 26 20v112a9 9 0 0 1-9 9H25a9 9 0 0 1-9-9V68c0-13 26-14 26-20z";
    const hx = 87, hy = 125, hr = 6.2;
    const hexPts = [0, 1, 2, 3, 4, 5].map((k) => { const a = Math.PI / 6 + k * Math.PI / 3; return [hx + hr * Math.cos(a), hy + hr * Math.sin(a)]; });
    const hexPath = hexPts.map((q, k) => `${k ? "L" : "M"}${q[0].toFixed(2)} ${q[1].toFixed(2)}`).join("") + "Z";
    const hexNodes = hexPts.map((q) => `<circle cx="${q[0].toFixed(2)}" cy="${q[1].toFixed(2)}" r=".95"/>`).join("");
    return `<svg class="vial ${opts.cls || ""}" viewBox="0 0 120 ${opts.shadow ? 206 : 196}" ${aria} xmlns="http://www.w3.org/2000/svg">${title}
<defs>
<linearGradient id="${id}g" x1="0" x2="1"><stop offset="0" stop-color="${acc}" stop-opacity=".42"/><stop offset=".08" stop-color="#ffffff" stop-opacity=".62"/><stop offset=".3" stop-color="#ffffff" stop-opacity=".14"/><stop offset=".7" stop-color="#ffffff" stop-opacity=".08"/><stop offset=".92" stop-color="#ffffff" stop-opacity=".46"/><stop offset="1" stop-color="${acc}" stop-opacity=".46"/></linearGradient>
<linearGradient id="${id}c" x1="0" x2="1"><stop offset="0" stop-color="#5d6672"/><stop offset=".12" stop-color="#c9cfd6"/><stop offset=".3" stop-color="#f4f6f9"/><stop offset=".48" stop-color="#9aa3ad"/><stop offset=".66" stop-color="#e7ebef"/><stop offset=".86" stop-color="#8d96a1"/><stop offset="1" stop-color="#4c545e"/></linearGradient>
<linearGradient id="${id}t" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#b9c1ca"/></linearGradient>
<linearGradient id="${id}l" x1="0" x2="1"><stop offset="0" stop-color="#020304"/><stop offset=".22" stop-color="#14181e"/><stop offset=".5" stop-color="#1c2129"/><stop offset=".78" stop-color="#11151b"/><stop offset="1" stop-color="#020304"/></linearGradient>
<linearGradient id="${id}w" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset=".5" stop-color="#dbe2ea"/><stop offset="1" stop-color="#8f9bab"/></linearGradient>
<linearGradient id="${id}p" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${powderTop}"/><stop offset="1" stop-color="${powderBot}"/></linearGradient>
<linearGradient id="${id}h" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".9"/><stop offset=".55" stop-color="#fff" stop-opacity=".22"/><stop offset="1" stop-color="#fff" stop-opacity=".65"/></linearGradient>
<clipPath id="${id}k"><path d="${body}"/></clipPath>
${opts.shadow ? `<filter id="${id}f" x="-50%" y="-200%" width="200%" height="500%"><feGaussianBlur stdDeviation="3.4"/></filter>` : ""}
</defs>
${opts.shadow ? `<ellipse cx="60" cy="196" rx="42" ry="4.6" fill="#0b1520" opacity=".3" filter="url(#${id}f)"/>` : ""}
<path d="${body}" fill="#ffffff" fill-opacity=".32"/>
<g clip-path="url(#${id}k)">
<rect x="16" y="40" width="88" height="150" fill="${acc}" fill-opacity=".07"/>
${water
  ? `<path d="M16 98q44 5 88 0v92H16z" fill="#bfe9f4" fill-opacity=".55"/><path d="M16 98q44 5 88 0" fill="none" stroke="#fff" stroke-opacity=".85" stroke-width="1.1"/>`
  : `<path d="M16 190v-19q44-9 88 0v19z" fill="url(#${id}p)"/><path d="M18 171.6q42-7.4 84 0" fill="none" stroke="#fff" stroke-opacity=".9" stroke-width="1"/>`}
<rect x="16" y="84" width="88" height="80" fill="url(#${id}l)"/>
<rect x="18.4" y="90" width=".9" height="68" fill="${acc}" opacity=".9"/>
<text x="24" y="98.5" font-family="${WIDE}" font-size="8.6" fill="url(#${id}w)" textLength="27" lengthAdjust="spacing">EGR</text>
<path d="M55.2 92.6l2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5z" fill="none" stroke="#c7d0da" stroke-width=".55"/>
<text x="24" y="103.6" font-family="${SANS}" font-size="2.6" font-weight="700" letter-spacing=".32" fill="#aab6c3">ENHANCED GENETIC RESEARCH</text>
<text x="24" y="122.5" font-family="${SANS}" font-size="${fs}" font-weight="800" letter-spacing=".2" fill="#ffffff"${fit}>${esc(name)}</text>
<rect x="24" y="127.4" width="33" height="1.9" rx=".95" fill="${acc}"/>
<path d="M57 128.35h14.5l4 -3.35h4.6" fill="none" stroke="${acc}" stroke-width=".6" opacity=".75"/>
<g fill="${acc}" stroke="${acc}" stroke-width=".7" opacity=".95"><path d="${hexPath}" fill="none"/>${hexNodes}<path d="M${hexPts[5][0].toFixed(2)} ${hexPts[5][1].toFixed(2)}l5.4-3.6M${hexPts[1][0].toFixed(2)} ${hexPts[1][1].toFixed(2)}v6" fill="none"/><circle cx="${(hexPts[5][0] + 5.4).toFixed(2)}" cy="${(hexPts[5][1] - 3.6).toFixed(2)}" r="1.05"/><circle cx="${hexPts[1][0].toFixed(2)}" cy="${(hexPts[1][1] + 6).toFixed(2)}" r="1.05"/></g>
<text x="24" y="138.6" font-family="${SANS}" font-size="6.6" font-weight="600" fill="#e8edf3">${esc(v.title)}</text>
<text x="24" y="148" font-family="${SANS}" font-size="3.1" font-weight="700" letter-spacing=".4" fill="#c6cfd9">RESEARCH USE ONLY</text>
<text x="24" y="152.6" font-family="${SANS}" font-size="3.1" font-weight="700" letter-spacing=".4" fill="#c6cfd9">NOT FOR HUMAN CONSUMPTION</text>
<text x="97" y="159.6" text-anchor="end" font-family="${SANS}" font-size="2.7" font-weight="600" letter-spacing=".3" fill="#7f8c9a">LOT ${esc(lt ? lt.number : "")}</text>
<rect x="16" y="84" width="88" height=".7" fill="#ffffff" opacity=".18"/>
<rect x="16" y="163.3" width="88" height=".7" fill="#ffffff" opacity=".12"/>
</g>
<path d="${body}" fill="url(#${id}g)" stroke="#5b6b7c" stroke-opacity=".38" stroke-width=".8"/>
<rect x="21.5" y="60" width="4.6" height="122" rx="2.3" fill="url(#${id}h)" opacity=".75"/>
<rect x="96.2" y="64" width="1.8" height="114" rx=".9" fill="#fff" opacity=".5"/>
<rect x="44.5" y="41" width="3" height="9" rx="1.5" fill="#fff" opacity=".6"/>
<rect x="31" y="29" width="58" height="12" rx="2" fill="url(#${id}c)"/>
<path d="M33 32.4h54M33 35.4h54M33 38.2h54" stroke="#4c545e" stroke-opacity=".22" stroke-width=".5"/>
<rect x="27" y="8" width="66" height="23" rx="5" fill="url(#${id}c)"/>
<rect x="28.2" y="5" width="63.6" height="7.4" rx="3.7" fill="url(#${id}t)"/>
<rect x="31" y="6.2" width="44" height="2" rx="1" fill="#fff" opacity=".7"/>
<rect x="27" y="28.4" width="66" height="2.6" rx="1.3" fill="#000" opacity=".16"/>
</svg>`;
  }
  EGR.vial = vial;

  /* Stage = soft light, floor, shadow, vial and reflection; cards and product pages */
  function stage(p, opts) {
    opts = opts || {};
    const v = opts.variant || p.variants[0];
    return `<span class="stage-glow" aria-hidden="true" style="--acc:${accentOf(p)}"></span><span class="stage-floor" aria-hidden="true"></span><span class="stage-shadow" aria-hidden="true"></span>
<span class="stage-reflect" aria-hidden="true">${vial(p, { variant: v, large: opts.large })}</span>
<span class="stage-vial">${vial(p, { variant: v, large: opts.large, title: `${p.title} ${v.title} vial` })}</span>`;
  }
  EGR.stage = stage;
  EGR.thumb = (p, v) => `<span class="thumb tone-${p.tone}" style="--acc:${accentOf(p)}">${vial(p, { variant: v })}</span>`;

  /* ------------------------------------------------------------- residues */
  const ONE = { A: "hydrophobic", V: "hydrophobic", L: "hydrophobic", I: "hydrophobic", M: "hydrophobic", F: "aromatic", W: "aromatic", Y: "aromatic", S: "polar", T: "polar", N: "polar", Q: "polar", C: "polar", K: "positive", R: "positive", H: "positive", D: "negative", E: "negative", G: "special", P: "special" };
  const THREE = { Ala: "A", Arg: "R", Asn: "N", Asp: "D", Cys: "C", Gln: "Q", Glu: "E", Gly: "G", His: "H", Ile: "I", Leu: "L", Lys: "K", Met: "M", Phe: "F", Pro: "P", Ser: "S", Thr: "T", Trp: "W", Tyr: "Y", Val: "V" };
  const SPECIAL = { Nle: "hydrophobic", "D-Phe": "aromatic", "D-2Nal": "aromatic", "2Nal": "aromatic", Aib: "nonstandard", "αMeL": "nonstandard", MeL: "nonstandard", "K*": "nonstandard" };
  function parseSequence(seq) {
    if (!seq) return null;
    let s = seq.split("·")[0].trim();
    s = s.replace(/-NH₂$|-NH2$/, "").replace(/^\([^)]*\)-/, "").replace(/^Ac-/, "").replace(/cyclo\[|\]/g, "");
    let tokens = [];
    if (s.indexOf("-") >= 0) {
      const raw = s.split("-").filter(Boolean);
      for (let i = 0; i < raw.length; i++) {
        if (raw[i] === "D" && raw[i + 1]) { tokens.push("D-" + raw[i + 1]); i++; } else tokens.push(raw[i]);
      }
    } else {
      const re = /\(([^)]+)\)|([A-Z])/g; let m;
      while ((m = re.exec(s))) tokens.push(m[1] || m[2]);
    }
    const residues = tokens.map((t) => {
      let cls = SPECIAL[t] || ONE[t] || (THREE[t] && ONE[THREE[t]]) || "nonstandard";
      const label = t === "αMeL" ? "MeL" : t;
      return { t: label, cls };
    });
    const multi = residues.some((r) => r.t.length > 1);
    return { residues, text: residues.map((r) => r.t).join(multi ? "-" : ""), length: residues.length };
  }
  EGR.parseSequence = parseSequence;
  EGR.residueClasses = [
    ["hydrophobic", "Hydrophobic"], ["aromatic", "Aromatic"], ["polar", "Polar"], ["positive", "Positive"],
    ["negative", "Negative"], ["special", "Gly / Pro"], ["nonstandard", "Non-standard"]
  ];
  function residueStrip(p, max) {
    max = max || 18;
    const seq = parseSequence(p.spec.sequence);
    if (seq) {
      const shown = seq.residues.slice(0, max).map((r) => `<i class="r-${r.cls}"></i>`).join("");
      const more = seq.length > max ? `<span class="more-n">+${seq.length - max}</span>` : "";
      return `<p class="res" title="${seq.length} residues: ${esc(seq.text)}" aria-label="${seq.length} residue sequence">${shown}${more}</p>`;
    }
    if (isBlend(p)) return `<p class="formula" title="${esc(p.components.map((c) => c.name).join(" + "))}">${esc(p.components.map((c) => c.name.replace(" (no DAC)", "")).join(" + "))}</p>`;
    if (p.spec.formula) return `<p class="formula" title="${esc(p.spec.formula)}">${esc(p.spec.formula)}</p>`;
    return `<p class="formula">${esc(p.spec.form)}</p>`;
  }
  EGR.residueStrip = residueStrip;

  /* --------------------------------------------------------- chromatogram */
  function chromData(lotNo, purity) {
    const r = rng(hash("chrom" + lotNo));
    const minutes = 30;
    const mainRt = Math.round((11.5 + r() * 6) * 100) / 100;
    const rest = Math.max(0, Math.round((100 - purity) * 100) / 100);
    const n = 2 + Math.floor(r() * 3);
    const w = Array.from({ length: n }, () => 0.4 + r());
    const sw = w.reduce((a, b) => a + b, 0);
    const peaks = [{ rt: mainRt, areaPct: purity, main: true }];
    let assigned = 0;
    const used = [];
    for (let i = 0; i < n; i++) {
      let area = i === n - 1 ? Math.round((rest - assigned) * 100) / 100 : Math.round((rest * w[i] / sw) * 100) / 100;
      assigned += area;
      let rt, tries = 0;
      do {
        const side = r() < 0.55 ? -1 : 1;
        rt = Math.round((mainRt + side * (1.3 + r() * 4.8)) * 100) / 100;
        tries++;
      } while (tries < 20 && used.some((u) => Math.abs(u - rt) < 0.9));
      used.push(rt);
      peaks.push({ rt, areaPct: Math.max(0.01, area), main: false });
    }
    const pts = [];
    for (let t = 0; t <= minutes + 1e-9; t += 0.04) {
      let y = 0.0016 + 0.0011 * Math.sin(t * 0.9 + r() * 0.2) + (r() - 0.5) * 0.0012;
      peaks.forEach((pk) => {
        const sigma = pk.main ? 0.11 : 0.12;
        const h = pk.main ? 1 : Math.min(0.05, (pk.areaPct / purity) * (0.11 / sigma) * 6);
        y += h * Math.exp(-((t - pk.rt) ** 2) / (2 * sigma * sigma));
      });
      pts.push([Math.round(t * 100) / 100, y]);
    }
    return { points: pts, peaks: peaks.sort((a, b) => a.rt - b.rt), minutes, mainRt };
  }
  function chromSvg(data, opts) {
    opts = opts || {};
    const W = 640, H = 214, X0 = 8, X1 = 632, Y0 = 26, YB = 182;
    const lo = Math.max(0, Math.floor((data.mainRt - 7) / 2) * 2);
    const hi = lo + 14;
    const x = (t) => X0 + ((t - lo) / (hi - lo)) * (X1 - X0);
    const y = (v) => YB - v * (YB - Y0 - 8);
    const pts = data.points.filter((p) => p[0] >= lo && p[0] <= hi);
    const d = pts.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join("");
    const gid = "cg" + (++uid);
    let grid = "";
    for (let t = lo; t <= hi; t += 2) {
      grid += `<line class="grid" x1="${x(t)}" x2="${x(t)}" y1="${Y0 - 4}" y2="${YB}"/><text class="tick" x="${x(t)}" y="${YB + 20}" text-anchor="middle">${t}</text>`;
    }
    const main = data.peaks.find((p) => p.main);
    const mx = x(main.rt), my = y(1);
    const right = mx < 440;
    const ann = `<line x1="${mx + (right ? 3 : -3)}" y1="${my - 1}" x2="${mx + (right ? 34 : -34)}" y2="${my - 13}" stroke="var(--ink)" stroke-width="1"/><text class="ann" x="${mx + (right ? 38 : -38)}" y="${my - 10}" text-anchor="${right ? "start" : "end"}">${main.rt.toFixed(2)} min · ${main.areaPct.toFixed(2)} %</text>`;
    let lastX = -999, row = 0;
    const imp = data.peaks.filter((p) => !p.main && p.rt >= lo && p.rt <= hi).map((p) => {
      const px = x(p.rt);
      row = Math.abs(px - lastX) < 46 ? (row ? 0 : 1) : 0;
      lastX = px;
      const ty = YB - 22 - row * 14;
      return `<line x1="${px}" x2="${px}" y1="${YB - 6}" y2="${ty + 4}" stroke="var(--ink-3)" stroke-width=".8"/><text class="ann-sm" x="${px}" y="${ty}" text-anchor="middle">${p.areaPct.toFixed(2)} %</text>`;
    }).join("");
    const label = opts.label || `HPLC chromatogram: main peak at ${main.rt.toFixed(2)} minutes, ${main.areaPct.toFixed(2)} percent of total area`;
    return `<svg class="chrom ${opts.animate ? "is-pending" : ""}" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(label)}">
<defs><linearGradient id="${gid}" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#0071e3" stop-opacity=".22"/><stop offset="1" stop-color="#0071e3" stop-opacity="0"/></linearGradient></defs>
${grid}<line class="axis" x1="${X0}" x2="${X1}" y1="${YB}" y2="${YB}"/>
<path class="area" d="${d}L${x(pts[pts.length - 1][0]).toFixed(1)},${YB}L${x(pts[0][0]).toFixed(1)},${YB}Z" fill="url(#${gid})"/>
<path class="trace" d="${d}" pathLength="1"/>
<g class="labels">${ann}${imp}</g>
</svg>`;
  }
  EGR.chrom = { data: chromData, svg: chromSvg };

  /* -------------------------------------------------------- product cards */
  /* Card: stage with quick view, name, size chips that switch price, lot and
     the add button in place, and a full-width add-to-cart button. */
  function sizeChips(p, sel, opts) {
    opts = opts || {};
    const daily = pricing.isDaily(p);
    if (p.variants.length < 2) {
      const v = p.variants[0];
      return `<div class="size-chips"><span class="size-chip is-static">${esc(v.title)}</span></div>`;
    }
    return `<div class="size-chips" role="group" aria-label="Choose a size for ${esc(p.title)}">${p.variants.map((v) => {
      const lt = activeLot(v);
      const price = daily ? pricing.dailyPrice(v) : v.price;
      return `<button type="button" class="size-chip" data-size="${v.id}" data-price="${price}" data-was="${daily ? v.price : ""}" data-lot="${lt ? lt.number : ""}" data-purity="${lt && lt.purity != null ? lt.purity.toFixed(2) : ""}" aria-pressed="${v === sel}">${esc(v.title)}</button>`;
    }).join("")}</div>`;
  }
  EGR.sizeChips = sizeChips;

  function coaLink(lt) {
    if (!lt) return "";
    const label = lt.purity != null ? `${lt.purity.toFixed(2)}%` : `Lot ${lt.number}`;
    return `<a class="pcard-coa" href="lot.html?lot=${lt.number}" data-coa aria-label="Certificate for lot ${lt.number}${lt.purity != null ? `, ${lt.purity.toFixed(2)} percent HPLC purity` : ""}"><span class="ok" aria-hidden="true">${icon("check")}</span><span data-coa-label>${label}</span><span class="lbl">COA</span></a>`;
  }

  function card(p, opts) {
    opts = opts || {};
    const v = p.variants[0];
    const lt = activeLot(v);
    const daily = pricing.isDaily(p);
    const price = daily ? pricing.dailyPrice(v) : v.price;
    const href = `product.html?p=${encodeURIComponent(p.handle)}`;
    let flag = "";
    if (daily) flag = `<span class="pcard-flag is-sale">Today −${P.daily.percent}%</span>`;
    else if (p.badges.indexOf("bestseller") >= 0) flag = `<span class="pcard-flag">Bestseller</span>`;
    else if (isBlend(p)) flag = `<span class="pcard-flag is-quiet">Blend</span>`;
    const soldOut = v.stock != null && v.stock <= 0;
    return `<article class="pcard ${opts.cls || ""}" data-handle="${esc(p.handle)}" style="--acc:${accentOf(p)}">
<div class="pcard-media">
<a class="stage tone-${p.tone}" href="${href}" aria-label="${esc(p.title)}, details" data-stage>${stage(p)}</a>
${flag}
<button type="button" class="pcard-quick" data-quick="${esc(p.handle)}" aria-label="Quick view ${esc(p.title)}">${icon("eye")}</button>
</div>
<div class="pcard-body">
<div class="pcard-head"><h3 class="pcard-name"><a href="${href}">${esc(p.title)}</a></h3>${coaLink(lt)}</div>
<p class="pcard-sub">${esc(p.subtitle)}</p>
${sizeChips(p, v)}
<div class="pcard-price"><span class="price" data-price-now>${money(price)}</span>${daily ? `<s data-price-was>${money(v.price)}</s>` : `<s data-price-was hidden></s>`}<span class="permg" data-permg>${esc(pricing.perMg(v, price))}</span></div>
<button type="button" class="btn btn-block pcard-add" data-add="${v.id}"${soldOut ? " disabled" : ""} aria-label="Add ${esc(p.title)} ${esc(v.title)} to cart">${icon("bag")}<span>${soldOut ? "Sold out" : "Add to cart"}</span></button>
</div>
</article>`;
  }
  function catalogSizes(p) { return p.variants.map((v) => v.title).join(" · "); }
  EGR.card = card;
  EGR.catalogSizes = catalogSizes;

  /* Size chip switching, shared by cards, quick view and the shelf. */
  document.addEventListener("click", (e) => {
    const chip = e.target.closest("[data-size]");
    if (!chip) return;
    const root = chip.closest("[data-handle]");
    const p = root && byHandle[root.dataset.handle];
    const ref = variantIndex[chip.dataset.size];
    if (!p || !ref) return;
    e.preventDefault();
    const v = ref.variant;
    root.querySelectorAll("[data-size]").forEach((c) => c.setAttribute("aria-pressed", c === chip));
    const price = parseFloat(chip.dataset.price);
    const now = root.querySelector("[data-price-now]");
    if (now) now.textContent = money(price);
    const was = root.querySelector("[data-price-was]");
    if (was) { if (chip.dataset.was) { was.textContent = money(parseFloat(chip.dataset.was)); was.hidden = false; } else was.hidden = true; }
    const pm = root.querySelector("[data-permg]");
    if (pm) pm.textContent = pricing.perMg(v, price);
    const add = root.querySelector("[data-add]");
    if (add) { const out = v.stock != null && v.stock <= 0; add.dataset.add = v.id; add.disabled = out; const t = add.querySelector("span"); if (t) t.textContent = out ? "Sold out" : "Add to cart"; add.setAttribute("aria-label", `Add ${p.title} ${v.title} to cart`); }
    const st = root.querySelector("[data-stage]");
    if (st) st.innerHTML = stage(p, { variant: v });
    const coa = root.querySelector("[data-coa]");
    const lt = activeLot(v);
    if (coa && lt) {
      coa.href = `lot.html?lot=${lt.number}`;
      const lbl = coa.querySelector("[data-coa-label]");
      if (lbl) lbl.textContent = lt.purity != null ? `${lt.purity.toFixed(2)}%` : `Lot ${lt.number}`;
    }
    root.dispatchEvent(new CustomEvent("egr:variant", { bubbles: true, detail: { product: p, variant: v } }));
  });

  /* Row for the lot tables */
  function lotRow(x, opts) {
    opts = opts || {};
    const l = x.lot, p = x.product, v = x.variant;
    const w = l.purity == null ? 0 : Math.max(4, Math.min(100, ((l.purity - 98) / 2) * 100));
    let ms = `<span class="ms"><span class="ok">${icon("check")}</span>Conforms</span>`;
    if (l.massObserved != null) { const [o, t] = fmtMassPair(l.massObserved, l.massTheoretical); ms = `<span class="ms"><span class="ok">${icon("check")}</span>${o} / ${t} Da</span>`; }
    else if (isBlend(p)) ms = `<span class="ms"><span class="ok">${icon("check")}</span>${p.components.length} components</span>`;
    const pur = l.purity == null ? `<span class="faint">n/a</span>` : `<div class="purity"><span class="purity-val">${l.purity.toFixed(2)} %</span><span class="meter" aria-hidden="true"><span style="width:${w}%"></span></span></div>`;
    const status = l.status === "archived" ? ` <span class="badge badge-archived">Archived</span>` : (opts.newest ? ` <span class="badge badge-ink">Newest</span>` : "");
    return `<tr data-href="lot.html?lot=${l.number}">
<td><a class="lotno" href="lot.html?lot=${l.number}">${l.number}</a>${status}</td>
<td><div class="cmp">${EGR.thumb(p, v)}<div><div class="cmp-name">${esc(p.title)}</div><div class="cmp-sub tnum">${esc(v.title)}</div></div></div></td>
<td>${pur}</td>
<td class="hide-sm">${ms}</td>
<td class="hide-sm tnum muted nowrap">${fmtDate(l.testedAt)}</td>
<td class="go"><a class="more small" href="lot.html?lot=${l.number}">View COA</a></td>
</tr>`;
  }
  EGR.lotRow = lotRow;
  EGR.lotTable = function (rows, opts) {
    opts = opts || {};
    return `<div class="table-card"><div class="table-scroll"><table class="lot-table">
<thead><tr><th scope="col">Lot</th><th scope="col">Compound</th><th scope="col">HPLC purity</th><th scope="col" class="hide-sm">Identity · MS</th><th scope="col" class="hide-sm">Tested</th><th class="go"><span class="sr-only">Certificate</span></th></tr></thead>
<tbody>${rows.map((x, i) => lotRow(x, { newest: opts.markNewest && i === 0 })).join("")}</tbody></table></div></div>`;
  };
})();
